import React, { createContext, useContext, useState, useCallback } from 'react';

// App-wide replacement for window.confirm()/window.alert(). Native browser
// dialogs are unreliable in embedded/webview browsers (confirmed: one such
// device showed the dialog for a split second and dismissed it on its own,
// silently taking the "cancelled" path for confirm(), and never letting the
// user read alert() messages at all) — this renders our own modal instead,
// which behaves identically everywhere since it's just React, not a native
// dialog.
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { icon, title, body, confirmText, cancelText, danger, alertOnly, resolve }

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { body: options } : options;
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const notify = useCallback((options) => {
    const opts = typeof options === 'string' ? { body: options } : options;
    return new Promise((resolve) => {
      setState({ icon: '⚠️', confirmText: 'OK', ...opts, alertOnly: true, resolve });
    });
  }, []);

  const handleChoice = (choice) => {
    state?.resolve(choice);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, notify }}>
      {children}
      {state && (
        <div
          className="modal-overlay"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }}
        >
          <div className="modal-content" style={{ maxWidth: 400, borderRadius: 28, padding: '40px 32px', textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 56, marginBottom: 24 }}>{state.icon || '❓'}</div>
            {state.title && <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{state.title}</h2>}
            <p style={{ margin: state.title ? '12px 0 32px' : '0 0 32px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {state.body}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: state.alertOnly ? '1fr' : '1fr 1fr', gap: 16 }}>
              {!state.alertOnly && (
                <button
                  className="btn btn-ghost"
                  style={{ borderRadius: 16, height: 54, fontWeight: 700, border: '1.5px solid var(--border)', background: 'white' }}
                  onClick={() => handleChoice(false)}
                >
                  {state.cancelText || 'Cancel'}
                </button>
              )}
              <button
                className="btn"
                style={{ borderRadius: 16, height: 54, fontWeight: 800, background: state.danger ? 'var(--danger)' : 'var(--success)', color: 'white', boxShadow: `0 10px 20px ${state.danger ? 'rgba(211,62,62,0.2)' : 'rgba(46,204,113,0.2)'}` }}
                onClick={() => handleChoice(true)}
              >
                {state.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// Returns an async confirm(options) => Promise<boolean> function — replaces
// window.confirm(). confirm("Plain message") or
// confirm({ title, body, icon, confirmText, cancelText, danger })
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx.confirm;
}

// Returns an async notify(options) => Promise<void> function — replaces
// window.alert(). Single OK button, no cancel choice.
// notify("Plain message") or notify({ title, body, icon })
export function useNotify() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useNotify must be used within a ConfirmProvider');
  return ctx.notify;
}
